import React, { useState, useEffect } from 'react';
import { FaUserFriends, FaTimes, FaPlus, FaEdit, FaTrash, FaSave } from 'react-icons/fa';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './AuthorizedPersonnelModal.module.css';
import { useTheme } from '../../contexts/ThemeContext';

const AuthorizedPersonnelModal = ({
  isOpen,
  onClose,
  selectedCustomer,
}) => {
  const { isDarkMode } = useTheme();
  const [authorizedPersonnel, setAuthorizedPersonnel] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [newPersonnel, setNewPersonnel] = useState({
    firstName: '',
    lastName: '',
    position: '',
    department: '',
    email: '',
    phone: '',
    notes: ''
  });

  // Yetkili personeli yükle
  const fetchAuthorizedPersonnel = async () => {
    if (!selectedCustomer) return;
    
    try {
      const personnelRef = collection(db, 'CustomerAuthorizedPersonnel');
      const q = query(
        personnelRef, 
        where('customerId', '==', selectedCustomer.id)
      );
      const querySnapshot = await getDocs(q);
      const personnelData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Client-side sorting
      personnelData.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          const aTime = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const bTime = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return bTime - aTime;
        }
        return 0;
      });
      
      setAuthorizedPersonnel(personnelData);
    } catch (error) {
      console.error('Error fetching authorized personnel:', error);
      toast.error('Yetkili personel yüklenirken hata oluştu');
    }
  };

  useEffect(() => {
    if (isOpen && selectedCustomer) {
      fetchAuthorizedPersonnel();
    }
  }, [isOpen, selectedCustomer]);

  // Yeni yetkili ekle
  const handleAddPersonnel = async (e) => {
    e.preventDefault();
    
    if (!newPersonnel.firstName.trim() || !newPersonnel.lastName.trim()) {
      toast.error('Ad ve soyad alanları zorunludur');
      return;
    }

    try {
      const personnelData = {
        ...newPersonnel,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer['Başlık'] || selectedCustomer['Müşteri Adı'] || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'CustomerAuthorizedPersonnel'), personnelData);
      toast.success('Yetkili personel başarıyla eklendi');
      setNewPersonnel({
        firstName: '',
        lastName: '',
        position: '',
        department: '',
        email: '',
        phone: '',
        notes: ''
      });
      setIsAddModalOpen(false);
      fetchAuthorizedPersonnel();
    } catch (error) {
      console.error('Error adding personnel:', error);
      toast.error('Yetkili personel eklenirken hata oluştu');
    }
  };

  // Yetkili düzenle
  const handleEditPersonnel = async (e) => {
    e.preventDefault();
    
    if (!editingPersonnel.firstName.trim() || !editingPersonnel.lastName.trim()) {
      toast.error('Ad ve soyad alanları zorunludur');
      return;
    }

    try {
      const personnelData = {
        ...editingPersonnel,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'CustomerAuthorizedPersonnel', editingPersonnel.id), personnelData);
      toast.success('Yetkili personel başarıyla güncellendi');
      setEditingPersonnel(null);
      setIsEditModalOpen(false);
      fetchAuthorizedPersonnel();
    } catch (error) {
      console.error('Error updating personnel:', error);
      toast.error('Yetkili personel güncellenirken hata oluştu');
    }
  };

  // Yetkili sil
  const handleDeletePersonnel = async (personnelId) => {
    if (window.confirm('Bu yetkili personeli silmek istediğinizden emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'CustomerAuthorizedPersonnel', personnelId));
        toast.success('Yetkili personel başarıyla silindi');
        fetchAuthorizedPersonnel();
      } catch (error) {
        console.error('Error deleting personnel:', error);
        toast.error('Yetkili personel silinirken hata oluştu');
      }
    }
  };

  // Düzenleme modalını aç
  const openEditModal = (personnel) => {
    setEditingPersonnel({ ...personnel });
    setIsEditModalOpen(true);
  };

  // Modal kapatma
  const closeModal = () => {
    onClose();
    setAuthorizedPersonnel([]);
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setEditingPersonnel(null);
  };

  // Overlay tıklama
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserFriends className={styles.modalIcon} />
            {`Yetkililer - ${selectedCustomer?.['Başlık'] || selectedCustomer?.['Müşteri Adı'] || ''}`}
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={closeModal}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.headerActions}>
            <button 
              className={styles.addButton}
              onClick={() => setIsAddModalOpen(true)}
            >
              <FaPlus className={styles.buttonIcon} />
              Yeni Yetkili Ekle
            </button>
          </div>

          <div className={styles.personnelList}>
            {authorizedPersonnel.length === 0 ? (
              <div className={styles.noPersonnel}>
                <FaUserFriends className={styles.noPersonnelIcon} />
                <p>Henüz yetkili personel eklenmemiş</p>
              </div>
            ) : (
              authorizedPersonnel.map((personnel) => (
                <div key={personnel.id} className={styles.personnelCard}>
                  <div className={styles.personnelHeader}>
                    <h3 className={styles.personnelName}>
                      {personnel.firstName} {personnel.lastName}
                    </h3>
                    <div className={styles.personnelActions}>
                      <button 
                        className={styles.editButton}
                        onClick={() => openEditModal(personnel)}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className={styles.deleteButton}
                        onClick={() => handleDeletePersonnel(personnel.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className={styles.personnelDetails}>
                    {personnel.position && (
                      <div className={styles.personnelInfo}>
                        <strong>Pozisyon:</strong> {personnel.position}
                      </div>
                    )}
                    {personnel.department && (
                      <div className={styles.personnelInfo}>
                        <strong>Departman:</strong> {personnel.department}
                      </div>
                    )}
                    {personnel.email && (
                      <div className={styles.personnelInfo}>
                        <strong>E-posta:</strong> {personnel.email}
                      </div>
                    )}
                    {personnel.phone && (
                      <div className={styles.personnelInfo}>
                        <strong>Telefon:</strong> {personnel.phone}
                      </div>
                    )}
                    {personnel.notes && (
                      <div className={styles.personnelNotes}>
                        <strong>Notlar:</strong> {personnel.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Yeni Yetkili Ekleme Modal */}
      {isAddModalOpen && (
        <div className={styles.subModalOverlay} onClick={(e) => e.target === e.currentTarget && setIsAddModalOpen(false)}>
          <div className={styles.subModalContent}>
            <div className={styles.subModalHeader}>
              <h3>Yeni Yetkili Ekle</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setIsAddModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddPersonnel} className={styles.personnelForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Ad *</label>
                  <input
                    type="text"
                    value={newPersonnel.firstName}
                    onChange={(e) => setNewPersonnel({...newPersonnel, firstName: e.target.value})}
                    required
                    placeholder="Ad"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Soyad *</label>
                  <input
                    type="text"
                    value={newPersonnel.lastName}
                    onChange={(e) => setNewPersonnel({...newPersonnel, lastName: e.target.value})}
                    required
                    placeholder="Soyad"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Pozisyon</label>
                  <input
                    type="text"
                    value={newPersonnel.position}
                    onChange={(e) => setNewPersonnel({...newPersonnel, position: e.target.value})}
                    placeholder="Pozisyon"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Departman</label>
                  <input
                    type="text"
                    value={newPersonnel.department}
                    onChange={(e) => setNewPersonnel({...newPersonnel, department: e.target.value})}
                    placeholder="Departman"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>E-posta</label>
                  <input
                    type="email"
                    value={newPersonnel.email}
                    onChange={(e) => setNewPersonnel({...newPersonnel, email: e.target.value})}
                    placeholder="E-posta adresi"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={newPersonnel.phone}
                    onChange={(e) => setNewPersonnel({...newPersonnel, phone: e.target.value})}
                    placeholder="Telefon numarası"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Notlar</label>
                <textarea
                  value={newPersonnel.notes}
                  onChange={(e) => setNewPersonnel({...newPersonnel, notes: e.target.value})}
                  rows="3"
                  placeholder="Ek notlar"
                />
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setIsAddModalOpen(false)}>
                  İptal
                </button>
                <button type="submit">
                  <FaSave className={styles.buttonIcon} />
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Yetkili Düzenleme Modal */}
      {isEditModalOpen && editingPersonnel && (
        <div className={styles.subModalOverlay} onClick={(e) => e.target === e.currentTarget && setIsEditModalOpen(false)}>
          <div className={styles.subModalContent}>
            <div className={styles.subModalHeader}>
              <h3>Yetkili Düzenle</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setIsEditModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleEditPersonnel} className={styles.personnelForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Ad *</label>
                  <input
                    type="text"
                    value={editingPersonnel.firstName}
                    onChange={(e) => setEditingPersonnel({...editingPersonnel, firstName: e.target.value})}
                    required
                    placeholder="Ad"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Soyad *</label>
                  <input
                    type="text"
                    value={editingPersonnel.lastName}
                    onChange={(e) => setEditingPersonnel({...editingPersonnel, lastName: e.target.value})}
                    required
                    placeholder="Soyad"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Pozisyon</label>
                  <input
                    type="text"
                    value={editingPersonnel.position}
                    onChange={(e) => setEditingPersonnel({...editingPersonnel, position: e.target.value})}
                    placeholder="Pozisyon"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Departman</label>
                  <input
                    type="text"
                    value={editingPersonnel.department}
                    onChange={(e) => setEditingPersonnel({...editingPersonnel, department: e.target.value})}
                    placeholder="Departman"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>E-posta</label>
                  <input
                    type="email"
                    value={editingPersonnel.email}
                    onChange={(e) => setEditingPersonnel({...editingPersonnel, email: e.target.value})}
                    placeholder="E-posta adresi"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={editingPersonnel.phone}
                    onChange={(e) => setEditingPersonnel({...editingPersonnel, phone: e.target.value})}
                    placeholder="Telefon numarası"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Notlar</label>
                <textarea
                  value={editingPersonnel.notes}
                  onChange={(e) => setEditingPersonnel({...editingPersonnel, notes: e.target.value})}
                  rows="3"
                  placeholder="Ek notlar"
                />
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setIsEditModalOpen(false)}>
                  İptal
                </button>
                <button type="submit">
                  <FaSave className={styles.buttonIcon} />
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorizedPersonnelModal;